import { useEffect, useRef, useState } from 'react';

/* Wraps window.CYRKIL_LISA_AVATAR — the existing, tested Three.js module
 * (public/assets/js/lisa-avatar-3d.js, unchanged from the static build)
 * — rather than rewriting the WebGL scene in React-idiomatic Three.js
 * bindings, per the task's "preserve the current UI exactly as much as
 * possible" instruction. Loaded only once, the first time the LISA
 * panel actually opens, via a plain <script type="module"> tag rather
 * than a JS `import()` — the file lives under public/ (a real ES module
 * importing Three.js from a sibling public/ path), and Vite's dev
 * server deliberately refuses to let source code `import()` a public/
 * asset (throws "should not be imported from source code"), so it has
 * to be fetched the same way the old static HTML fetched it: as a
 * plain module script tag the browser requests directly. */
let scriptLoadPromise: Promise<void> | null = null;
function loadAvatarScript(): Promise<void> {
  if (window.CYRKIL_LISA_AVATAR) return Promise.resolve();
  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = '/assets/js/lisa-avatar-3d.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load LISA 3D avatar script'));
      document.head.appendChild(script);
    });
  }
  return scriptLoadPromise;
}

declare global {
  interface Window {
    CYRKIL_LISA_AVATAR?: {
      mount(el: HTMLElement): boolean;
      start(): void;
      stop(): void;
      setState(s: 'idle' | 'listening' | 'thinking' | 'speaking'): void;
      isMounted(): boolean;
    };
  }
}

export function useLisaAvatar3D(containerRef: React.RefObject<HTMLDivElement>) {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const attemptedRef = useRef(false);

  async function ensureMounted() {
    if (ready || failed || attemptedRef.current) return;
    attemptedRef.current = true;
    try {
      await loadAvatarScript();
      const api = window.CYRKIL_LISA_AVATAR;
      if (!api || !containerRef.current || !window.WebGLRenderingContext) {
        setFailed(true);
        return;
      }
      const ok = api.isMounted() || api.mount(containerRef.current);
      if (ok) {
        api.start();
        setReady(true);
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }
  }

  useEffect(() => {
    return () => {
      window.CYRKIL_LISA_AVATAR?.stop();
    };
  }, []);

  function setState(s: 'idle' | 'listening' | 'thinking' | 'speaking') {
    if (ready) window.CYRKIL_LISA_AVATAR?.setState(s);
  }

  return { ready, failed, ensureMounted, setState };
}
